import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Grid,
    TextField,
    Typography
} from '@material-ui/core';
import { observer } from 'mobx-react';
import React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { LocalDate } from '../../../core/api/date';
import { getSitekey } from '../../../core/common/recaptcha';
import { injectTSDI } from '../../../core/common/tsdi';
import { I18n } from '../../../core/i18n';
import { StudioSelect } from '../../components/inputs/studio-select';
import { SharedContractDissolutionStore } from '../../shared-contract-dissolution-store';
import { StyledBlackLabel, StyledReCAPTCHA } from '../../styles';
import { ContractTerminationStore } from '../contract-termination-store';

export const MemberDataForm = observer(() => {
    const recaptchaRef: React.RefObject<ReCAPTCHA> =
        React.createRef<ReCAPTCHA>();
    const { translate } = injectTSDI(I18n);
    const sharedStore = injectTSDI(SharedContractDissolutionStore);
    const { handleMemberDataSubmit, cancelContractManualForm } = injectTSDI(
        ContractTerminationStore
    );
    const { firstname, lastname, dateOfBirth, customerNumber } =
        sharedStore.memberDataForm.wrappedField;

    return (
        <Card variant="outlined">
            <CardHeader
                title={translate('public.content.terminate.contract.leave')}
                titleTypographyProps={{
                    variant: 'h5',
                    component: 'h2'
                }}
            />
            <CardContent>
                <Grid container spacing={5}>
                    <Grid item xs={12}>
                        <Typography>
                            {translate(
                                'public.content.terminate.contract.info'
                            )}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography>
                            {translate('public.content.common.studio.which')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <StudioSelect
                            studioId={
                                cancelContractManualForm.wrappedField.studioId
                            }
                            onStudioSelected={() =>
                                cancelContractManualForm.wrappedField.cancelationReasonId.reset(
                                    undefined
                                )
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Grid
                            container
                            justifyContent="center"
                            alignItems="flex-start"
                            spacing={3}
                        >
                            <Grid item xs={6} sm={4}>
                                <TextField
                                    id="first-name"
                                    fullWidth
                                    required
                                    value={firstname.value}
                                    onChange={(event) =>
                                        firstname.reset(event.target.value)
                                    }
                                    error={Boolean(firstname.error)}
                                    helperText={translate(firstname.error)}
                                    label={
                                        <StyledBlackLabel>
                                            {translate(
                                                'public.content.common.firstname'
                                            )}
                                        </StyledBlackLabel>
                                    }
                                    variant="outlined"
                                    inputProps={{
                                        'data-role': 'first-name'
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4}>
                                <TextField
                                    id="last-name"
                                    fullWidth
                                    required
                                    label={
                                        <StyledBlackLabel>
                                            {translate(
                                                'public.content.common.lastname'
                                            )}
                                        </StyledBlackLabel>
                                    }
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    value={lastname.value}
                                    onChange={(event) =>
                                        lastname.reset(event.target.value)
                                    }
                                    error={Boolean(lastname.error)}
                                    helperText={translate(lastname.error)}
                                    inputProps={{ 'data-role': 'last-name' }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    id="date-of-birth"
                                    fullWidth
                                    required
                                    label={
                                        <StyledBlackLabel>
                                            {translate(
                                                'public.content.common.dob'
                                            )}
                                        </StyledBlackLabel>
                                    }
                                    variant="outlined"
                                    type="date"
                                    inputProps={{
                                        max: LocalDate.now()
                                            .subDays(1)
                                            .toString(),
                                        'data-role': 'date-of-birth'
                                    }}
                                    InputLabelProps={{
                                        shrink: true
                                    }}
                                    value={dateOfBirth.value.toString()}
                                    onChange={(event) =>
                                        sharedStore.updateDateIfValid(
                                            event,
                                            dateOfBirth
                                        )
                                    }
                                    error={Boolean(dateOfBirth.error)}
                                    helperText={translate(dateOfBirth.error)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    id="member-id"
                                    fullWidth
                                    label={
                                        <StyledBlackLabel>
                                            {translate(
                                                'public.content.common.memberid'
                                            )}
                                        </StyledBlackLabel>
                                    }
                                    variant="outlined"
                                    value={customerNumber.value || ''}
                                    onChange={(event) =>
                                        customerNumber.reset(
                                            event.target.value || undefined
                                        )
                                    }
                                    inputProps={{
                                        'data-role': 'member-id'
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    helperText={translate(
                                        'public.content.terminate.contract.show.contracts.info'
                                    )}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    data-role="submit-member-data"
                                    disabled={Boolean(
                                        !customerNumber.value ||
                                            !customerNumber.value.trim()
                                    )}
                                    onClick={() =>
                                        handleMemberDataSubmit(recaptchaRef)
                                    }
                                >
                                    {translate(
                                        'public.content.terminate.contract.show.contracts'
                                    )}
                                </Button>
                            </Grid>
                            <StyledReCAPTCHA
                                sitekey={getSitekey()}
                                ref={recaptchaRef}
                                size="invisible"
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
});
